import { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Chip, Collapse, IconButton, Menu, MenuItem,
  Drawer, TextField, Select, FormControl, InputLabel, Button, Divider,
  Autocomplete,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AssetsWidget from '../components/widgets/AssetsWidget';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import GridViewIcon from '@mui/icons-material/GridView';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { vlans, getVlanForIP, ipInSubnet } from '../data/networkData';
import { useAssets } from '../hooks/useAssets';

function getSubnet24(ip) {
  const parts = ip.split('.');
  return `${parts[0]}.${parts[1]}.${parts[2]}.0`;
}

// ── IP Range Row ───────────────────────────────────────────────

function IPRow({ entry, onEdit, onSelectDevice, onSelectPurpose }) {
  const isUnassigned = entry.status === 'unassigned';
  const isOffline = entry.status === 'offline';
  const [hovered, setHovered] = useState(false);

  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onEdit}
      sx={{
        display: 'grid',
        gridTemplateColumns: '120px 140px 160px 140px 30px',
        alignItems: 'center',
        padding: '3px 12px',
        borderBottom: '1px solid #1a2840',
        cursor: 'pointer',
        '&:hover': { backgroundColor: isUnassigned ? '#0a1525' : '#0d1f33' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Typography variant="body2" sx={{ color: '#00bcd4', fontFamily: 'monospace', fontSize: 11 }}>{entry.ip}</Typography>
        {entry.annotation && (
          <Chip label={entry.annotation} size="small" sx={{ height: 16, fontSize: 9, fontWeight: 700, backgroundColor: '#1e3a5f', color: '#7a8fa6' }} />
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {entry.hostname ? (
          <Typography
            variant="body2"
            onClick={(e) => { e.stopPropagation(); onSelectDevice && onSelectDevice(entry); }}
            sx={{ color: '#ffffff', fontSize: 11, cursor: 'pointer', '&:hover': { color: '#00bcd4', textDecoration: 'underline' } }}
          >
            {entry.hostname}
          </Typography>
        ) : (
          <Typography variant="body2" sx={{ color: '#2a3a50', fontSize: 12 }}>—</Typography>
        )}
        {hovered && !isUnassigned && (
          <EditIcon sx={{ fontSize: 11, color: '#00bcd4', opacity: 0.7 }} />
        )}
      </Box>
      <Typography variant="body2" sx={{ color: isUnassigned ? '#2a3a50' : '#7a8fa6', fontSize: 11 }}>
        {entry.os || '—'}
      </Typography>
      {entry.purpose && !isUnassigned && onSelectPurpose ? (
        <Typography
          variant="body2"
          onClick={(e) => { e.stopPropagation(); onSelectPurpose(entry.purpose); }}
          sx={{ color: '#7a8fa6', fontSize: 11, cursor: 'pointer', '&:hover': { color: '#00bcd4', textDecoration: 'underline' } }}
        >
          {entry.purpose}
        </Typography>
      ) : (
        <Typography variant="body2" sx={{ color: isUnassigned ? '#2a3a50' : '#7a8fa6', fontSize: 11 }}>
          {entry.purpose || '—'}
        </Typography>
      )}
      <FiberManualRecordIcon sx={{
        fontSize: 10,
        color: isUnassigned ? '#2a3a50' : isOffline ? '#f44336' : '#4caf50',
      }} />
    </Box>
  );
}

// ── Device Group Widget ────────────────────────────────────────

function DeviceRow({ device, onSelectDevice }) {
  const [expanded, setExpanded] = useState(false);
  const hasMultipleIPs = device.ips.length > 1;

  return (
    <Box sx={{ borderBottom: '1px solid #1a2840' }}>
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: '140px 1fr 90px 70px 28px',
        alignItems: 'center',
        padding: '5px 12px',
        '&:hover': { backgroundColor: '#0d1f33', cursor: 'pointer' },
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FiberManualRecordIcon sx={{ fontSize: 8, color: device.offline ? '#f44336' : '#4caf50' }} />
          <Typography
            variant="body2"
            onClick={(e) => { e.stopPropagation(); onSelectDevice && onSelectDevice(device); }}
            sx={{ color: '#ffffff', fontWeight: 600, fontSize: 11, cursor: 'pointer', '&:hover': { color: '#00bcd4', textDecoration: 'underline' } }}
          >
            {device.hostname}
          </Typography>
        </Box>
        <Box>
          <Typography variant="body2" sx={{ color: '#00bcd4', fontFamily: 'monospace', fontSize: 11 }}>{device.ips[0]}</Typography>
          {hasMultipleIPs && !expanded && (
            <Typography variant="caption" sx={{ color: '#7a8fa6', fontSize: 10 }}>+{device.ips.length - 1} more</Typography>
          )}
        </Box>
        <Typography variant="body2" sx={{ color: '#7a8fa6', fontSize: 10 }}>{device.lastSeen}</Typography>
        <Chip label={device.type} size="small" sx={{
          backgroundColor: device.type === 'Virtual' ? '#2a1f4a' : '#1e3a5f',
          color: device.type === 'Virtual' ? '#a855f7' : '#00bcd4',
          fontSize: 10, fontWeight: 700, height: 20,
        }} />
        {hasMultipleIPs && (
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }} sx={{ color: '#7a8fa6', padding: 0 }}>
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        )}
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ paddingLeft: '28px', paddingBottom: 1 }}>
          {device.ips.slice(1).map((ip) => (
            <Typography key={ip} variant="caption" sx={{ color: '#7a8fa6', fontFamily: 'monospace', display: 'block', fontSize: 11 }}>
              └ {ip}
            </Typography>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

function SortableDeviceGroup({ group, onSelectDevice }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: group.id });

  return (
    <Paper
      ref={setNodeRef}
      sx={{
        backgroundColor: '#111827',
        border: '1px solid #1e3a5f',
        borderRadius: 2,
        marginBottom: 2,
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
        position: 'relative',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid #1e3a5f' }}>
        <Typography variant="subtitle1" sx={{ color: '#ffffff', fontWeight: 700 }}>{group.label}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip label={`${group.devices.length} devices`} size="small" sx={{ backgroundColor: '#1e3a5f', color: '#7a8fa6', fontSize: 11 }} />
          <Box {...attributes} {...listeners} sx={{ color: '#1e3a5f', cursor: 'grab', '&:hover': { color: '#00bcd4' }, display: 'flex', alignItems: 'center' }}>
            <DragIndicatorIcon fontSize="small" />
          </Box>
        </Box>
      </Box>

      {/* Column Headers */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '140px 1fr 90px 70px 28px', padding: '4px 12px', borderBottom: '1px solid #1a2840' }}>
        {['Hostname', 'IP Address', 'Last Seen', 'Type', ''].map((col) => (
          <Typography key={col} variant="caption" sx={{ color: '#7a8fa6', fontWeight: 700, fontSize: 10, textTransform: 'uppercase' }}>{col}</Typography>
        ))}
      </Box>

      {/* Rows */}
      {group.devices.map((device) => (
        <DeviceRow key={device.hostname} device={device} onSelectDevice={onSelectDevice} />
      ))}
    </Paper>
  );
}

// ── Column Headers with Dropdown Filters ──────────────────────

// These are populated dynamically via props passed from the main component
const uniqueOSList = ['All'];
const uniquePurposeList = ['All'];

function FilterHeader({ label, options, value, onChange }) {
  const [anchor, setAnchor] = useState(null);
  const isFiltered = value.length > 0;

  const toggle = (val) => {
    onChange(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
      <Typography variant="caption" sx={{ color: isFiltered ? '#00bcd4' : '#7a8fa6', fontWeight: 700, fontSize: 10, textTransform: 'uppercase' }}>
        {label}{isFiltered ? ` (${value.length})` : ''}
      </Typography>
      {options && (
        <>
          <Box onClick={(e) => setAnchor(e.currentTarget)} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', color: isFiltered ? '#00bcd4' : '#1e3a5f', '&:hover': { color: '#00bcd4' } }}>
            <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
          </Box>
          <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
            PaperProps={{ sx: { backgroundColor: '#111827', border: '1px solid #1e3a5f', minWidth: 160 } }}>
            {isFiltered && (
              <MenuItem onClick={() => onChange([])} sx={{ fontSize: 11, color: '#f44336', borderBottom: '1px solid #1e3a5f', '&:hover': { backgroundColor: '#0d2137' } }}>
                Clear filter
              </MenuItem>
            )}
            {options.map(({ label: optLabel, val }) => {
              const selected = value.includes(val);
              return (
                <MenuItem key={val} onClick={() => toggle(val)}
                  sx={{ fontSize: 12, color: selected ? '#00bcd4' : '#c0cfe0', backgroundColor: selected ? '#0d2137' : 'transparent', '&:hover': { backgroundColor: '#0d2137' }, display: 'flex', justifyContent: 'space-between' }}>
                  {optLabel}
                  {selected && <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#00bcd4' }} />}
                </MenuItem>
              );
            })}
          </Menu>
        </>
      )}
    </Box>
  );
}

function ColumnHeaders({ filterOS, setFilterOS, filterStatus, setFilterStatus, filterAssigned, setFilterAssigned, filterPurpose, setFilterPurpose, filterVlan, setFilterVlan, uniqueOSList, uniquePurposeList }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '120px 140px 160px 140px 30px', padding: '4px 12px', borderBottom: '1px solid #1a2840' }}>
      <FilterHeader label="IP Address"
        value={filterVlan}
        options={vlans.map(v => ({ label: `${v.id} — ${v.name}`, val: v.subnet }))}
        onChange={setFilterVlan}
      />
      <FilterHeader label="Hostname"
        value={filterAssigned}
        options={[
          { label: 'Assigned', val: 'assigned' },
          { label: 'Unassigned', val: 'unassigned' },
        ]}
        onChange={setFilterAssigned}
      />
      <FilterHeader label="OS"
        value={filterOS}
        options={uniqueOSList.filter(o => o !== 'All').map((os) => ({ label: os, val: os }))}
        onChange={setFilterOS}
      />
      <FilterHeader label="Purpose"
        value={filterPurpose}
        options={uniquePurposeList.filter(p => p !== 'All').map((p) => ({ label: p, val: p }))}
        onChange={setFilterPurpose}
      />
      <FilterHeader label="Status"
        value={filterStatus}
        options={[
          { label: 'Online', val: 'online' },
          { label: 'Offline', val: 'offline' },
          { label: 'Unassigned', val: 'unassigned' },
        ]}
        onChange={setFilterStatus}
      />
    </Box>
  );
}

// ── Edit / Assign Drawer ───────────────────────────────────────

const FIELD_SX = {
  '& .MuiOutlinedInput-root': {
    color: '#c0cfe0',
    fontSize: 13,
    '& fieldset': { borderColor: '#1e3a5f' },
    '&:hover fieldset': { borderColor: '#00bcd4' },
    '&.Mui-focused fieldset': { borderColor: '#00bcd4' },
  },
  '& .MuiInputLabel-root': { color: '#7a8fa6', fontSize: 13 },
  '& .MuiInputLabel-root.Mui-focused': { color: '#00bcd4' },
  '& .MuiSelect-icon': { color: '#7a8fa6' },
};

function EditDrawer({ entry, onClose, onSave, knownOS = [], knownPurposes = [], knownTypes = [] }) {
  const isUnassigned = !entry || entry.status === 'unassigned';
  const [form, setForm] = useState({
    hostname: entry?.hostname || '',
    os: entry?.os || '',
    purpose: entry?.purpose || '',
    type: entry?.type || '',
    status: entry?.status === 'unassigned' ? 'online' : (entry?.status || 'online'),
    vlan: (() => {
      if (!entry) return '';
      const v = getVlanForIP(entry.ip);
      return v ? v.subnet : '';
    })(),
    notes: entry?.notes || '',
    additionalIPs: entry?.additionalIPs || [],
  });

  const set = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  const handleSave = () => {
    onSave(entry.ip, { ...form, type: form.type || entry?.type || 'Physical' });
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={Boolean(entry)}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 380,
          backgroundColor: '#0d1825',
          borderLeft: '1px solid #1e3a5f',
          padding: 0,
        },
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #1e3a5f' }}>
        <Box>
          <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 700, fontSize: 15 }}>
            {isUnassigned ? 'Assign IP' : 'Edit Device'}
          </Typography>
          <Typography variant="caption" sx={{ color: '#00bcd4', fontFamily: 'monospace', fontSize: 12 }}>
            {entry?.ip}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: '#7a8fa6', '&:hover': { color: '#ffffff' } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Form */}
      <Box sx={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: 2.5, overflowY: 'auto' }}>

        <TextField
          label="Hostname"
          value={form.hostname}
          onChange={set('hostname')}
          size="small"
          fullWidth
          placeholder="e.g. SRV-APP02"
          sx={FIELD_SX}
        />

        <Autocomplete
          freeSolo
          options={knownOS}
          value={form.os}
          onInputChange={(_, val) => setForm(prev => ({ ...prev, os: val }))}
          renderInput={(params) => (
            <TextField {...params} label="Operating System" size="small" placeholder="e.g. Windows Server 2022" sx={FIELD_SX} />
          )}
          componentsProps={{ paper: { sx: { backgroundColor: '#111827', border: '1px solid #1e3a5f', color: '#c0cfe0' } } }}
          sx={{ '& .MuiAutocomplete-option': { color: '#c0cfe0' } }}
        />

        <Autocomplete
          freeSolo
          options={knownPurposes}
          value={form.purpose}
          onInputChange={(_, val) => setForm(prev => ({ ...prev, purpose: val }))}
          renderInput={(params) => (
            <TextField {...params} label="Purpose" size="small" placeholder="e.g. App Server" sx={FIELD_SX} />
          )}
          componentsProps={{ paper: { sx: { backgroundColor: '#111827', border: '1px solid #1e3a5f', color: '#c0cfe0' } } }}
          sx={{ '& .MuiAutocomplete-option': { color: '#c0cfe0' } }}
        />

        <Autocomplete
          freeSolo
          options={knownTypes}
          value={form.type}
          onInputChange={(_, val) => setForm(prev => ({ ...prev, type: val }))}
          renderInput={(params) => (
            <TextField {...params} label="Type" size="small" placeholder="e.g. Physical" sx={FIELD_SX} />
          )}
          componentsProps={{ paper: { sx: { backgroundColor: '#111827', border: '1px solid #1e3a5f', color: '#c0cfe0' } } }}
          sx={{ '& .MuiAutocomplete-option': { color: '#c0cfe0' } }}
        />

        <FormControl size="small" fullWidth sx={FIELD_SX}>
          <InputLabel>Status</InputLabel>
          <Select value={form.status} onChange={set('status')} label="Status"
            MenuProps={{ PaperProps: { sx: { backgroundColor: '#111827', border: '1px solid #1e3a5f' } } }}>
            <MenuItem value="online" sx={{ color: '#4caf50', fontSize: 13 }}>Online</MenuItem>
            <MenuItem value="offline" sx={{ color: '#f44336', fontSize: 13 }}>Offline</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" fullWidth sx={FIELD_SX}>
          <InputLabel>VLAN</InputLabel>
          <Select value={form.vlan} onChange={set('vlan')} label="VLAN"
            MenuProps={{ PaperProps: { sx: { backgroundColor: '#111827', border: '1px solid #1e3a5f' } } }}>
            <MenuItem value="" sx={{ color: '#7a8fa6', fontSize: 13 }}>— Unassigned —</MenuItem>
            {vlans.map((v) => (
              <MenuItem key={v.subnet} value={v.subnet} sx={{ fontSize: 13, color: '#c0cfe0' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: v.color, flexShrink: 0 }} />
                  {v.id} — {v.name} ({v.subnet})
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Notes"
          value={form.notes}
          onChange={set('notes')}
          size="small"
          fullWidth
          multiline
          rows={3}
          placeholder="Optional notes..."
          sx={FIELD_SX}
        />

        <Divider sx={{ borderColor: '#1e3a5f' }} />

        {/* Additional IPs */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 1.5 }}>
            <Typography sx={{ color: '#7a8fa6', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>Additional IPs</Typography>
            <Button
              size="small"
              startIcon={<AddIcon sx={{ fontSize: 13 }} />}
              onClick={() => setForm(prev => ({ ...prev, additionalIPs: [...prev.additionalIPs, { ip: '', label: 'MGT' }] }))}
              sx={{ color: '#00bcd4', fontSize: 11, padding: '2px 8px', minWidth: 0, '&:hover': { backgroundColor: '#0d2137' } }}
            >
              Add IP
            </Button>
          </Box>

          {form.additionalIPs.length === 0 && (
            <Typography sx={{ color: '#2a3a50', fontSize: 12 }}>No additional IPs assigned.</Typography>
          )}

          {form.additionalIPs.map((item, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center', marginBottom: 1 }}>
              <FormControl size="small" sx={{ width: 90, ...FIELD_SX }}>
                <InputLabel sx={{ fontSize: 11 }}>Label</InputLabel>
                <Select
                  value={item.label}
                  label="Label"
                  onChange={(e) => setForm(prev => {
                    const ips = [...prev.additionalIPs];
                    ips[i] = { ...ips[i], label: e.target.value };
                    return { ...prev, additionalIPs: ips };
                  })}
                  MenuProps={{ PaperProps: { sx: { backgroundColor: '#111827', border: '1px solid #1e3a5f' } } }}
                >
                  {['MGT', 'VIP', 'Secondary', 'Loopback'].map(l => (
                    <MenuItem key={l} value={l} sx={{ color: '#c0cfe0', fontSize: 12 }}>{l}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="IP Address"
                value={item.ip}
                placeholder="e.g. 10.1.0.50"
                onChange={(e) => setForm(prev => {
                  const ips = [...prev.additionalIPs];
                  ips[i] = { ...ips[i], ip: e.target.value };
                  return { ...prev, additionalIPs: ips };
                })}
                sx={{ flex: 1, ...FIELD_SX }}
              />
              <IconButton
                size="small"
                onClick={() => setForm(prev => ({ ...prev, additionalIPs: prev.additionalIPs.filter((_, j) => j !== i) }))}
                sx={{ color: '#f44336', opacity: 0.6, '&:hover': { opacity: 1 } }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          ))}

          {form.additionalIPs.length > 0 && (
            <Box sx={{ marginTop: 1, padding: '8px 10px', backgroundColor: '#0a1525', borderRadius: 1, border: '1px solid #1e3a5f' }}>
              <Typography sx={{ color: '#4a5a70', fontSize: 11 }}>Hostname, OS, and Type are inherited from the primary IP.</Typography>
            </Box>
          )}
        </Box>

        <Divider sx={{ borderColor: '#1e3a5f' }} />

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="contained"
            onClick={handleSave}
            fullWidth
            sx={{
              backgroundColor: '#00bcd4',
              color: '#000000',
              fontWeight: 700,
              fontSize: 13,
              '&:hover': { backgroundColor: '#00acc1' },
            }}
          >
            Save
          </Button>
          <Button
            variant="outlined"
            onClick={onClose}
            fullWidth
            sx={{
              borderColor: '#1e3a5f',
              color: '#7a8fa6',
              fontSize: 13,
              '&:hover': { borderColor: '#7a8fa6', color: '#c0cfe0' },
            }}
          >
            Cancel
          </Button>
        </Box>

        {/* Future: API status note */}
        <Typography variant="caption" sx={{ color: '#2a3a50', fontSize: 10, textAlign: 'center' }}>
          Changes saved locally · API integration pending
        </Typography>

      </Box>
    </Drawer>
  );
}

// ── Add / Delete Range Dialogs ─────────────────────────────────

function parseCIDR(cidr) {
  const m = cidr.trim().match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})\/(\d{1,2})$/);
  if (!m) return null;
  const octets = [+m[1], +m[2], +m[3], +m[4]];
  const prefix = +m[5];
  if (octets.some(o => o > 255) || prefix < 8 || prefix > 30) return null;
  // Mask base IP to network address
  const baseNum = ((octets[0] << 24) | (octets[1] << 16) | (octets[2] << 8) | octets[3]) >>> 0;
  const mask = prefix === 0 ? 0 : (~0 << (32 - prefix)) >>> 0;
  const net = (baseNum & mask) >>> 0;
  const base = [(net >>> 24) & 255, (net >>> 16) & 255, (net >>> 8) & 255, net & 255];
  const count = Math.pow(2, 32 - prefix);
  return { base, prefix, count, cidr: `${base.join('.')}/${prefix}` };
}

function generateCustomRange(parsed) {
  const { base, prefix, count } = parsed;
  const baseNum = (base[0] << 24) | (base[1] << 16) | (base[2] << 8) | base[3];
  return Array.from({ length: count }, (_, i) => {
    const n = (baseNum + i) >>> 0;
    const ip = [(n >>> 24) & 255, (n >>> 16) & 255, (n >>> 8) & 255, n & 255].join('.');
    return { ip, hostname: null, os: null, purpose: null, status: 'unassigned', type: null };
  });
}

function AddRangeDialog({ open, onClose, onAdd }) {
  const [cidr, setCidr] = useState('');
  const [label, setLabel] = useState('');

  const parsed = parseCIDR(cidr);
  const tooLarge = parsed && parsed.count > 4096;
  const valid = parsed && !tooLarge;

  const handleAdd = () => {
    if (!valid) return;
    const ips = generateCustomRange(parsed);
    onAdd({ id: parsed.cidr, cidr: parsed.cidr, label: label.trim() || parsed.cidr, ips });
    setCidr(''); setLabel('');
    onClose();
  };

  const handleClose = () => { setCidr(''); setLabel(''); onClose(); };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{ sx: { width: 360, backgroundColor: '#0d1825', borderLeft: '1px solid #1e3a5f' } }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #1e3a5f' }}>
        <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 700, fontSize: 15 }}>Add IP Range</Typography>
        <IconButton onClick={handleClose} size="small" sx={{ color: '#7a8fa6', '&:hover': { color: '#ffffff' } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <TextField
          label="CIDR Notation"
          value={cidr}
          onChange={(e) => setCidr(e.target.value)}
          size="small"
          fullWidth
          placeholder="e.g. 192.168.1.0/24"
          inputProps={{ style: { fontFamily: 'monospace' } }}
          error={cidr.length > 0 && !parsed}
          helperText={
            cidr.length === 0 ? 'Enter a valid CIDR (e.g. 10.2.0.0/23)' :
            !parsed ? 'Invalid CIDR — use format x.x.x.x/prefix (prefix /8–/30)' :
            tooLarge ? `Too large — ${parsed.count.toLocaleString()} IPs exceeds the 4096 limit` :
            `${parsed.count.toLocaleString()} IPs · network address: ${parsed.cidr}`
          }
          FormHelperTextProps={{ sx: { color: !parsed && cidr.length > 0 ? '#f44336' : tooLarge ? '#ff9800' : '#4caf50', fontSize: 11 } }}
          sx={FIELD_SX}
        />

        <TextField
          label="Label (optional)"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          size="small"
          fullWidth
          placeholder="e.g. DMZ Network"
          sx={FIELD_SX}
        />

        <Divider sx={{ borderColor: '#1e3a5f' }} />

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="contained"
            onClick={handleAdd}
            disabled={!valid}
            fullWidth
            sx={{ backgroundColor: '#00bcd4', color: '#000', fontWeight: 700, fontSize: 13, '&:hover': { backgroundColor: '#00acc1' }, '&.Mui-disabled': { backgroundColor: '#1e3a5f', color: '#4a5a70' } }}
          >
            Add Range
          </Button>
          <Button
            variant="outlined"
            onClick={handleClose}
            fullWidth
            sx={{ borderColor: '#1e3a5f', color: '#7a8fa6', fontSize: 13, '&:hover': { borderColor: '#7a8fa6', color: '#c0cfe0' } }}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}

function DeleteRangeDialog({ range, onClose, onConfirm }) {
  const [typed, setTyped] = useState('');
  const confirmed = typed.trim() === range?.cidr;

  const handleConfirm = () => { if (confirmed) { onConfirm(range.id); onClose(); } };
  const handleClose = () => { setTyped(''); onClose(); };

  return (
    <Drawer
      anchor="right"
      open={Boolean(range)}
      onClose={handleClose}
      PaperProps={{ sx: { width: 380, backgroundColor: '#0d1825', borderLeft: '1px solid #1e3a5f' } }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #1e3a5f' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon sx={{ color: '#ff9800', fontSize: 18 }} />
          <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 700, fontSize: 15 }}>Delete Range</Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" sx={{ color: '#7a8fa6', '&:hover': { color: '#ffffff' } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Box sx={{ padding: '12px 14px', backgroundColor: '#1a0a0a', border: '1px solid #4a1515', borderRadius: 1 }}>
          <Typography variant="body2" sx={{ color: '#f44336', fontWeight: 700, fontSize: 13, marginBottom: 0.5 }}>
            This action is permanent.
          </Typography>
          <Typography variant="body2" sx={{ color: '#c0cfe0', fontSize: 12 }}>
            Deleting <Box component="span" sx={{ fontFamily: 'monospace', color: '#00bcd4' }}>{range?.cidr}</Box> will remove{' '}
            <strong>{range?.ips?.length?.toLocaleString()}</strong> IP entries and all manually assigned data within this range from your view.
          </Typography>
        </Box>

        <Box>
          <Typography variant="caption" sx={{ color: '#7a8fa6', fontSize: 11, display: 'block', marginBottom: 1 }}>
            Type the CIDR range to confirm:
          </Typography>
          <TextField
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            size="small"
            fullWidth
            placeholder={range?.cidr}
            inputProps={{ style: { fontFamily: 'monospace' } }}
            sx={{
              ...FIELD_SX,
              '& .MuiOutlinedInput-root': {
                ...FIELD_SX['& .MuiOutlinedInput-root'],
                '& fieldset': { borderColor: confirmed ? '#4caf50' : '#1e3a5f' },
                '&.Mui-focused fieldset': { borderColor: confirmed ? '#4caf50' : '#f44336' },
              },
            }}
          />
        </Box>

        <Divider sx={{ borderColor: '#1e3a5f' }} />

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={!confirmed}
            fullWidth
            sx={{ backgroundColor: '#c62828', color: '#fff', fontWeight: 700, fontSize: 13, '&:hover': { backgroundColor: '#b71c1c' }, '&.Mui-disabled': { backgroundColor: '#1e3a5f', color: '#4a5a70' } }}
          >
            Delete Range
          </Button>
          <Button
            variant="outlined"
            onClick={handleClose}
            fullWidth
            sx={{ borderColor: '#1e3a5f', color: '#7a8fa6', fontSize: 13, '&:hover': { borderColor: '#7a8fa6', color: '#c0cfe0' } }}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}

// ── Custom Range Tab ───────────────────────────────────────────

function CustomRangeTab({ range, isActive, onSelect, onDelete }) {
  const [hovered, setHovered] = useState(false);
  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        display: 'flex', alignItems: 'center', gap: 0.5,
        padding: '4px 8px 4px 10px',
        borderRadius: '6px 6px 0 0',
        cursor: 'pointer',
        backgroundColor: isActive ? '#0d2137' : 'transparent',
        borderTop: isActive ? '1px solid #ff9800' : '1px solid transparent',
        borderLeft: isActive ? '1px solid #1e3a5f' : '1px solid transparent',
        borderRight: isActive ? '1px solid #1e3a5f' : '1px solid transparent',
        borderBottom: isActive ? '1px solid #0d2137' : '1px solid transparent',
        marginBottom: isActive ? '-1px' : 0,
        '&:hover': { backgroundColor: '#0d2137' },
      }}
      onClick={onSelect}
    >
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#ff9800', flexShrink: 0 }} />
      <Typography variant="caption" sx={{ color: isActive ? '#ff9800' : '#7a8fa6', fontFamily: 'monospace', fontWeight: isActive ? 700 : 400, fontSize: 11 }}>
        {range.label !== range.cidr ? `${range.label} (${range.cidr})` : range.cidr}
      </Typography>
      <Box
        component="span"
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        sx={{
          display: 'flex', alignItems: 'center',
          color: hovered ? '#f44336' : '#2a3a50',
          transition: 'color 0.15s',
          marginLeft: 0.25,
          '&:hover': { color: '#f44336' },
        }}
      >
        <DeleteOutlineIcon sx={{ fontSize: 13 }} />
      </Box>
    </Box>
  );
}

// ── Assets Page ────────────────────────────────────────────────

export default function Assets({ onSelectDevice, onSelectGroup, localOverrides, setLocalOverrides, customRanges, setCustomRanges }) {
  const { ipRange, deviceGroups, summary, loading, error } = useAssets();
  const subnets      = ['all', ...Array.from(new Set(ipRange.map((e) => getSubnet24(e.ip))))];
  const knownOS      = Array.from(new Set(ipRange.map(e => e.os).filter(Boolean))).sort();
  const knownPurposes= Array.from(new Set(ipRange.map(e => e.purpose).filter(Boolean))).sort();
  const knownTypes   = Array.from(new Set(ipRange.map(e => e.type).filter(Boolean))).sort();

  const [groupOrder, setGroupOrder] = useState([]);
  const [activeId, setActiveId] = useState(null);

  // Sync group order when API data loads
  useEffect(() => { if (deviceGroups.length > 0 && groupOrder.length === 0) setGroupOrder(deviceGroups.map(g => g.id)); }, [deviceGroups]); // eslint-disable-line
  const [selectedSubnet, setSelectedSubnet] = useState('all');
  const [twoColumn, setTwoColumn] = useState(true);
  const [filterAssigned, setFilterAssigned] = useState([]);
  const [filterStatus, setFilterStatus] = useState([]);
  const [filterOS, setFilterOS] = useState([]);
  const [filterPurpose, setFilterPurpose] = useState([]);
  const [filterVlan, setFilterVlan] = useState([]);
  const [editEntry, setEditEntry] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleSaveEntry = (ip, data) => {
    setLocalOverrides(prev => ({ ...prev, [ip]: data }));
  };

  const handleAddRange = (range) => {
    setCustomRanges(prev => [...prev, range]);
  };

  const handleDeleteRange = (id) => {
    setCustomRanges(prev => prev.filter(r => r.id !== id));
    if (selectedSubnet === id) setSelectedSubnet('all');
  };

  // All IPs: base range + custom ranges + additional IPs from overrides
  const baseIPSet = new Set([
    ...ipRange.map(e => e.ip),
    ...customRanges.flatMap(r => r.ips.map(e => e.ip)),
  ]);
  const extraIPs = Object.entries(localOverrides).flatMap(([primaryIP, data]) =>
    (data.additionalIPs || [])
      .filter(e => e.ip && !baseIPSet.has(e.ip))
      .map(e => ({
        ip: e.ip,
        hostname: data.hostname,
        os: data.os,
        type: data.type,
        purpose: data.purpose,
        status: data.status || 'online',
        annotation: e.label,
        _primaryIP: primaryIP,
      }))
  );
  const allIPs = [...ipRange, ...customRanges.flatMap(r => r.ips), ...extraIPs];

  const headerStats = (() => {
    let view;
    if (selectedSubnet === 'all') view = allIPs;
    else if (customRanges.some(r => r.cidr === selectedSubnet)) view = allIPs.filter(i => ipInSubnet(i.ip, selectedSubnet));
    else view = allIPs.filter(i => getSubnet24(i.ip) === selectedSubnet);
    const merged = view.map(i => localOverrides[i.ip] ? { ...i, ...localOverrides[i.ip] } : i);
    return {
      assigned: merged.filter(i => i.status !== 'unassigned').length,
      unassigned: merged.filter(i => i.status === 'unassigned').length,
    };
  })();

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const handleDragEnd = ({ active, over }) => {
    setActiveId(null);
    if (!over || active.id === over.id) return;
    setGroupOrder((prev) => arrayMove(prev, prev.indexOf(active.id), prev.indexOf(over.id)));
  };

  const orderedGroups = groupOrder.map((id) => deviceGroups.find((g) => g.id === id));
  const activeGroup = activeId ? deviceGroups.find((g) => g.id === activeId) : null;

  return (
    <Box sx={{ backgroundColor: '#0a0e1a', minHeight: '100vh', padding: 3, display: 'flex', gap: 3, alignItems: 'stretch' }}>

      {/* Left — IP Range */}
      <Paper sx={{ backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2, width: 740, flexShrink: 0, display: 'flex', flexDirection: 'column', position: 'sticky', top: 0, height: 'calc(100vh - 112px)' }}>

        {/* Header */}
        <Box sx={{ padding: '12px 14px 0', borderBottom: '1px solid #1e3a5f' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 700 }}>IP Range</Typography>
              <Typography variant="caption" sx={{ color: '#7a8fa6' }}>
                {headerStats.assigned} assigned · {headerStats.unassigned} unassigned
              </Typography>
            </Box>
            <IconButton
              onClick={() => setAddDialogOpen(true)}
              size="small"
              title="Add IP Range"
              sx={{ color: '#1e3a5f', border: '1px solid #1e3a5f', borderRadius: 1, padding: '3px', '&:hover': { color: '#00bcd4', borderColor: '#00bcd4', backgroundColor: '#0d2137' } }}
            >
              <AddIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>

          {/* Subnet Tabs */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            {/* Base /24 tabs */}
            {subnets.map((subnet) => {
              const isActive = selectedSubnet === subnet;
              const label = subnet === 'all' ? 'All' : `${subnet.slice(0, -1)}x /24`;
              return (
                <Box
                  key={subnet}
                  onClick={() => setSelectedSubnet(subnet)}
                  sx={{
                    padding: '4px 10px',
                    borderRadius: '6px 6px 0 0',
                    cursor: 'pointer',
                    backgroundColor: isActive ? '#0d2137' : 'transparent',
                    borderTop: isActive ? '1px solid #00bcd4' : '1px solid transparent',
                    borderLeft: isActive ? '1px solid #1e3a5f' : '1px solid transparent',
                    borderRight: isActive ? '1px solid #1e3a5f' : '1px solid transparent',
                    borderBottom: isActive ? '1px solid #0d2137' : '1px solid transparent',
                    marginBottom: isActive ? '-1px' : 0,
                    '&:hover': { color: '#00bcd4' },
                  }}
                >
                  <Typography variant="caption" sx={{
                    color: isActive ? '#00bcd4' : '#7a8fa6',
                    fontFamily: subnet === 'all' ? 'inherit' : 'monospace',
                    fontWeight: isActive ? 700 : 400,
                    fontSize: 11,
                  }}>
                    {label}
                  </Typography>
                </Box>
              );
            })}

            {/* Custom range tabs */}
            {customRanges.map((range) => {
              const isActive = selectedSubnet === range.cidr;
              return (
                <CustomRangeTab
                  key={range.id}
                  range={range}
                  isActive={isActive}
                  onSelect={() => setSelectedSubnet(range.cidr)}
                  onDelete={() => setDeleteTarget(range)}
                />
              );
            })}
          </Box>
        </Box>

        {/* Column Headers with inline filters */}
        <ColumnHeaders
          filterOS={filterOS} setFilterOS={setFilterOS}
          filterStatus={filterStatus} setFilterStatus={setFilterStatus}
          filterAssigned={filterAssigned} setFilterAssigned={setFilterAssigned}
          filterPurpose={filterPurpose} setFilterPurpose={setFilterPurpose}
          filterVlan={filterVlan} setFilterVlan={setFilterVlan}
          uniqueOSList={uniqueOSList}
          uniquePurposeList={uniquePurposeList}
        />

        {/* IP Rows */}
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {(() => {
            const isCustomTab = customRanges.some(r => r.cidr === selectedSubnet);
            const merged = allIPs.map(e => localOverrides[e.ip] ? { ...e, ...localOverrides[e.ip] } : e);
            const filtered = merged
              .filter((e) => selectedSubnet === 'all' || (isCustomTab ? ipInSubnet(e.ip, selectedSubnet) : getSubnet24(e.ip) === selectedSubnet))
              .filter((e) => filterVlan.length === 0 || filterVlan.some(subnet => ipInSubnet(e.ip, subnet)))
              .filter((e) => filterAssigned.length === 0 || filterAssigned.some(v => v === 'assigned' ? e.status !== 'unassigned' : e.status === 'unassigned'))
              .filter((e) => filterStatus.length === 0 || filterStatus.includes(e.status))
              .filter((e) => filterOS.length === 0 || filterOS.includes(e.os))
              .filter((e) => filterPurpose.length === 0 || filterPurpose.includes(e.purpose));

            return filtered.map((entry, i) => {
              const prevVlan = i > 0 ? getVlanForIP(filtered[i - 1].ip) : null;
              const nextVlan = i < filtered.length - 1 ? getVlanForIP(filtered[i + 1].ip) : null;
              const vlan = getVlanForIP(entry.ip);
              const isFirstInSubnet = prevVlan?.id !== vlan?.id;
              const isLastInSubnet = nextVlan?.id !== vlan?.id;

              const VlanBand = ({ label }) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, padding: '4px 12px', backgroundColor: '#0a0e1a' }}>
                  <Box sx={{ flex: 1, height: '2px', backgroundColor: vlan?.color || '#1e3a5f' }} />
                  <Typography variant="caption" sx={{ color: vlan?.color || '#1e3a5f', fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {label}
                  </Typography>
                  <Box sx={{ flex: 1, height: '2px', backgroundColor: vlan?.color || '#1e3a5f' }} />
                </Box>
              );

              return (
                <Box key={entry.ip}>
                  {isFirstInSubnet && (
                    <VlanBand label={vlan ? `▶ ${vlan.id} · ${vlan.name} · ${vlan.subnet}` : `▶ Unknown`} />
                  )}
                  <IPRow entry={entry} onEdit={() => setEditEntry(localOverrides[entry.ip] ? { ...entry, ...localOverrides[entry.ip] } : entry)} onSelectDevice={onSelectDevice} onSelectPurpose={onSelectGroup ? (purpose) => onSelectGroup({ id: 'purpose-' + purpose, label: purpose, type: 'purpose', value: purpose }) : null} />
                  {isLastInSubnet && (
                    <VlanBand label={vlan ? `${vlan.id} · ${vlan.subnet} ◀` : `Unknown ◀`} />
                  )}
                </Box>
              );
            });
          })()}
        </Box>
      </Paper>

      {/* Right — Summary + Sortable Device Group Widgets */}
      <Box sx={{ flex: 1 }}>

        {/* Assets Overview Widget */}
        <Paper sx={{ backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2, marginBottom: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid #1e3a5f' }}>
            <Typography variant="subtitle1" sx={{ color: '#ffffff', fontWeight: 700 }}>Assets Overview</Typography>
          </Box>
          <AssetsWidget onNavigate={onSelectGroup} />
        </Paper>

        {/* Layout Toggle */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 2, gap: 0.5 }}>
          <IconButton
            onClick={() => setTwoColumn(false)}
            size="small"
            sx={{ color: !twoColumn ? '#00bcd4' : '#1e3a5f', backgroundColor: !twoColumn ? '#0d2137' : 'transparent', borderRadius: 1, '&:hover': { color: '#00bcd4' } }}
          >
            <ViewStreamIcon fontSize="small" />
          </IconButton>
          <IconButton
            onClick={() => setTwoColumn(true)}
            size="small"
            sx={{ color: twoColumn ? '#00bcd4' : '#1e3a5f', backgroundColor: twoColumn ? '#0d2137' : 'transparent', borderRadius: 1, '&:hover': { color: '#00bcd4' } }}
          >
            <GridViewIcon fontSize="small" />
          </IconButton>
        </Box>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={({ active }) => setActiveId(active.id)}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={groupOrder} strategy={twoColumn ? rectSortingStrategy : verticalListSortingStrategy}>
            <Box sx={twoColumn ? { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 } : {}}>
              {orderedGroups.map((group) => (
                <SortableDeviceGroup key={group.id} group={group} onSelectDevice={onSelectDevice} />
              ))}
            </Box>
          </SortableContext>

          <DragOverlay>
            {activeGroup && (
              <Paper sx={{ backgroundColor: '#111827', border: '1px solid #00bcd4', borderRadius: 2, padding: 2, opacity: 0.9 }}>
                <Typography variant="body2" sx={{ color: '#00bcd4', fontWeight: 700 }}>{activeGroup.label}</Typography>
              </Paper>
            )}
          </DragOverlay>
        </DndContext>
      </Box>

      {/* Edit / Assign Drawer */}
      {editEntry && (
        <EditDrawer
          entry={editEntry}
          onClose={() => setEditEntry(null)}
          onSave={handleSaveEntry}
          knownOS={knownOS}
          knownPurposes={knownPurposes}
          knownTypes={knownTypes}
        />
      )}

      {/* Add Range Drawer */}
      <AddRangeDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onAdd={handleAddRange}
      />

      {/* Delete Range Confirmation Drawer */}
      {deleteTarget && (
        <DeleteRangeDialog
          range={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDeleteRange}
        />
      )}

    </Box>
  );
}
